import type { MusicalToken, TimeSignature } from '../../types/music';
import type { ScoreRenderEvent } from './vexFlowMapper';
import type { PhraseScoreBar } from './VexPhraseScore';

export interface PhraseScorePayloadLike {
  phraseBarNumbers?: number[];
  barTokenStarts?: number[];
  patternTokens?: MusicalToken[];
  rhythmDots?: number[];
  scoreEventsByBar?: Record<number, ScoreRenderEvent[]>;
  scoreEvents?: ScoreRenderEvent[];
  modifiers?: { ties?: [number, number][] };
}

function perBarSliceIndices(globalIndices: number[], tokenStart: number, tokenEnd: number): number[] {
  return globalIndices.filter((i) => i >= tokenStart && i < tokenEnd).map((i) => i - tokenStart);
}

function perBarTiePairs(globalTies: [number, number][], tokenStart: number, tokenEnd: number): [number, number][] {
  return globalTies
    .filter(([from, to]) => from >= tokenStart && to < tokenEnd)
    .map(([from, to]) => [from - tokenStart, to - tokenStart] as [number, number]);
}

/** Construye entradas por compás para VexPhraseScore desde un payload de guía/examen. */
export function buildPhraseScoreBars(payload: PhraseScorePayloadLike): PhraseScoreBar[] {
  const phraseBars = payload.phraseBarNumbers?.length ? payload.phraseBarNumbers : null;
  if (!phraseBars?.length) return [];

  const tokens = payload.patternTokens ?? [];
  const starts = payload.barTokenStarts ?? [];
  const globalDots = payload.rhythmDots ?? [];
  const globalTies = payload.modifiers?.ties ?? [];
  const byBar = payload.scoreEventsByBar ?? {};

  return phraseBars.map((barNumber, barIdx) => {
    const tokenStart = starts[barIdx] ?? 0;
    const tokenEnd = starts[barIdx + 1] ?? tokens.length;
    const barTokens = tokens.slice(tokenStart, tokenEnd);
    const scoreEvents =
      byBar[barNumber] ??
      (byBar as Record<string, ScoreRenderEvent[] | undefined>)[String(barNumber)];

    return {
      barNumber,
      scoreEvents: scoreEvents?.length ? scoreEvents : undefined,
      patternTokens: scoreEvents?.length ? undefined : barTokens,
      dottedIndices: perBarSliceIndices(globalDots, tokenStart, tokenEnd),
      intraBarTies: perBarTiePairs(globalTies, tokenStart, tokenEnd),
    };
  });
}

export function phraseScoreCaption(
  phraseBarNumbers: number[],
  timeSignature: TimeSignature = '4/4',
): string {
  if (!phraseBarNumbers.length) return `Compás ${timeSignature}`;
  if (phraseBarNumbers.length === 1) {
    return `Compás ${phraseBarNumbers[0]} (${timeSignature})`;
  }
  return `Compases ${phraseBarNumbers[0]}–${phraseBarNumbers[phraseBarNumbers.length - 1]} (${timeSignature})`;
}
